// POST /compat/checks · GET /compat/checks · GET /compat/checks/:id
import { z } from 'zod'
import {
  compatAccuracySchema,
  compatPrecisionSchema,
  compatTypeSchema,
  genderSchema,
  zodiacSignSchema,
} from '../enums.js'
import {
  idSchema,
  isoDateSchema,
  isoDateTimeSchema,
  paginated,
  paginationQuerySchema,
  timeOfDaySchema,
} from './common.js'

// Одна сфера разбора со шкалой (см. domain/compatScore.ts getSpheresByCompatType для ключей).
export const compatSphereSchema = z.object({
  key: z.string(),
  value: z.number().int().min(0).max(100),
})
export type CompatSphere = z.infer<typeof compatSphereSchema>

export const compatCheckDtoSchema = z.object({
  id: idSchema,
  type: compatTypeSchema,
  target_name: z.string(),
  target_gender: genderSchema,
  accuracy: compatAccuracySchema,
  target_date: isoDateSchema.nullable(), // null в режиме «только знак»
  target_sign: zodiacSignSchema.nullable(),
  target_time: timeOfDaySchema.nullable(),
  target_city_id: idSchema.nullable(),
  // Чем расчёт РАСПОЛАГАЛ по каждой карте. Две величины, а не одна: деградация у сторон разная,
  // и строка под шкалами обязана назвать, чьих именно данных не хватило.
  own_precision: compatPrecisionSchema,
  target_precision: compatPrecisionSchema,
  // Версия математики (`COMPAT_CALC_VERSION`). Проверки, посчитанные прежней формулой, остаются
  // в истории как есть — экран помечает их, а не пересчитывает.
  calc_version: z.number().int().min(1),
  score: z.number().int().min(30).max(100),
  verdict: z.string(), // строка-приговор из курируемого пула
  spheres: z.array(compatSphereSchema),
  analysis: z.string(), // генеративный текст «Разбор Луни»
  created_at: isoDateTimeSchema,
})
export type CompatCheckDto = z.infer<typeof compatCheckDtoSchema>

// POST /compat/checks — шаг 2 → 3. Обязательны имя, пол, (дата или знак).
export const createCompatCheckRequestSchema = z
  .object({
    type: compatTypeSchema,
    target_name: z.string().min(1).max(64),
    target_gender: genderSchema,
    accuracy: compatAccuracySchema,
    target_date: isoDateSchema.optional(),
    target_sign: zodiacSignSchema.optional(),
    target_time: timeOfDaySchema.optional(), // «точнее, если знаешь»
    target_city_id: idSchema.optional(),
  })
  .refine(
    (body) =>
      body.accuracy === 'date' ? body.target_date !== undefined : body.target_sign !== undefined,
    {
      message: 'В режиме "date" нужна target_date, в режиме "sign" — target_sign',
      path: ['accuracy'],
    },
  )
export type CreateCompatCheckRequest = z.infer<typeof createCompatCheckRequestSchema>
export const createCompatCheckResponseSchema = compatCheckDtoSchema
export type CreateCompatCheckResponse = z.infer<typeof createCompatCheckResponseSchema>

// GET /compat/checks — история (живёт в Профиле).
export const listCompatChecksQuerySchema = paginationQuerySchema
export type ListCompatChecksQuery = z.infer<typeof listCompatChecksQuerySchema>
export const listCompatChecksResponseSchema = paginated(compatCheckDtoSchema)
export type ListCompatChecksResponse = z.infer<typeof listCompatChecksResponseSchema>

// GET /compat/checks/:id — повторный просмотр, результат не пересчитывается (детерминизм).
export const getCompatCheckResponseSchema = compatCheckDtoSchema
export type GetCompatCheckResponse = z.infer<typeof getCompatCheckResponseSchema>
